'use client';

import type { CSSProperties, ReactNode } from 'react';
import {
  Card, HeroImage, MediaHeader, ScreenBody, Stack, StickyBar, TabBar,
  type TabBarAction, type TabBarItem, type TabBarOverflowItem, type Tone,
} from '@leanin/ui';
import { BodyWithAside } from './BodyWithAside';
import styles from './DetailPage.module.css';

export type DetailPageProps = {
  // Identity / hero / banner block, full width above any tab strip.
  // Caller-provided ReactNode — different detail pages need different
  // visuals (avatar hero, tone-tinted header, identity card, etc), so the
  // shell owns the surrounding chrome but stays out of the header's content.
  header: ReactNode;

  // When true, the header bleeds edge-to-edge — outer ScreenBody padding
  // is removed so HeroImage / tone-tinted banners can span full width.
  // The body still gets horizontal padding internally. Default false.
  // Implied true when `cover` is set.
  headerBleed?: boolean;

  // Optional hero banner rendered above the header. The banner bleeds edge-
  // to-edge; the `header` ReactNode is wrapped in a white Card that overlaps
  // the bottom of the banner by `overlap` px and aligns with the body's
  // horizontal padding. The canonical chrome-on-cover treatment used across
  // detail pages. When `src` is provided the banner is a photographic
  // HeroImage; otherwise a tone-tinted MediaHeader stands in.
  //
  // When `overlap` is omitted the stylesheet default applies (80, dropping
  // to 60 below 720px). An explicit `overlap` applies at every width — pick
  // one comfortably below the header card's minimum height, or the card's
  // bottom edge lands flush with (or above) the banner's and sub-pixel
  // rounding at fractional zoom shows a sliver of banner beneath the card.
  cover?: { src: string; height?: number; overlap?: number } | { tone: Tone; height?: number; overlap?: number };

  // Optional sub-tabs region. When provided, the shell pins them in a
  // StickyBar directly under the header — no gap between header and tabs.
  tabs?: ReadonlyArray<TabBarItem>;
  activeTab?: string;
  onTabChange?: (id: string) => void;

  // Page-wide actions (Edit, Add member, kebab, etc.) rendered at the right
  // end of the TabBar row. Only meaningful when `tabs` is provided — when
  // there are no tabs, the action chrome belongs INSIDE the header card via
  // `PageHeader.actions`, not floating in an empty sticky band below it.
  //
  // Actions always render inline at the right of the tab strip; the tabs
  // strip gets whatever width is left. If an action belongs in the menu
  // rather than as a visible button, put it in `overflowActions`.
  actions?: ReadonlyArray<TabBarAction>;
  // Items that always live in the action overflow menu — never render
  // inline. Use for items that are meaningful as a menu entry but not as
  // a button (e.g., "Leader tips", "Mute notifications").
  overflowActions?: ReadonlyArray<TabBarOverflowItem>;

  // Body content. Stacks vertically with gap=5 ('var(--space-lg)'). When no tabs, the
  // body gets paddingTop 'var(--space-xl)' for breathing room from the header;
  // with tabs, the StickyBar's paddingBlock handles that rhythm.
  children: ReactNode;

  // Optional sidebar rail to the right of the body. Composed via
  // `BodyWithAside`, which handles the container-query collapse below
  // ~660px (body min 320 + gap 24 + aside 320).
  aside?: ReactNode;

  // Optional content rendered inside a position:relative wrapper on top of
  // the cover banner. Used for the cover photo upload button on profile pages.
  coverOverlay?: ReactNode;
};

export function DetailPage({
  header,
  headerBleed = false,
  cover,
  tabs,
  activeTab,
  onTabChange,
  actions,
  overflowActions,
  children,
  aside,
  coverOverlay,
}: DetailPageProps) {
  const hasTabs = tabs !== undefined && tabs.length > 0;

  const mainColumn = (
    <Stack
      gap="none"
      style={{
        background: 'var(--background)',
        overflow: 'clip',
        minWidth: 0,
      }}
    >
      {hasTabs ? (
        <StickyBar>
          <TabBar
            items={tabs as TabBarItem[]}
            {...(activeTab !== undefined ? { value: activeTab } : {})}
            {...(onTabChange !== undefined ? { onChange: onTabChange } : {})}
            {...(actions !== undefined ? { actions } : {})}
            {...(overflowActions !== undefined ? { overflowActions } : {})}
          />
        </StickyBar>
      ) : null}
      <Stack
        gap="lg"
        style={{
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- conditional padding (zero when tabs absorb the gap, else SpaceKey 6); ternary doesn't fit Box prop API
          paddingTop: hasTabs ? 0 : 'var(--space-xl)',
        }}
      >
        {children}
      </Stack>
    </Stack>
  );

  const bodyRegion = aside ? (
    <BodyWithAside
      aside={
        <div className={styles.aside}>
          {aside}
        </div>
      }
    >
      {mainColumn}
    </BodyWithAside>
  ) : (
    mainColumn
  );

  let inner: ReactNode;
  if (cover) {
    const overlap = cover.overlap ?? 80;
    const height = cover.height ?? 280;
    const banner = 'src' in cover ? (
      <HeroImage src={cover.src} height={height} />
    ) : (
      <MediaHeader tone={cover.tone} height={height} />
    );
    const bannerWithOverlay = coverOverlay ? (
      <div style={{ position: 'relative' }}>
        {banner}
        {coverOverlay}
      </div>
    ) : banner;
    inner = (
      <ScreenBody
        padX="none"
        padTop="none"
        gap="none"
        responsivePadX={false}
        style={{ background: 'var(--background)', minHeight: '100%' }}
      >
        {bannerWithOverlay}
        <div className={styles.body}>
          <Card
            pad="default"
            className={overlap > 0 ? styles.card : undefined}
            style={{
              position: 'relative',
              background: 'var(--card)',
              ...(cover.overlap !== undefined && cover.overlap > 0
                ? ({ '--detail-card-overlap': `-${cover.overlap}px` } as CSSProperties)
                : {}),
            }}
          >
            {header}
          </Card>
          {bodyRegion}
        </div>
      </ScreenBody>
    );
  } else if (headerBleed) {
    inner = (
      <ScreenBody
        padX="none"
        padTop="none"
        gap="none"
        style={{ background: 'var(--background)', minHeight: '100%' }}
      >
        {header}
        <div className={styles.body}>{bodyRegion}</div>
      </ScreenBody>
    );
  } else {
    inner = (
      <ScreenBody
        gap="none"
        style={{ background: 'var(--background)', minHeight: '100%' }}
      >
        {header}
        {bodyRegion}
      </ScreenBody>
    );
  }

  return inner;
}
